import { useMutation, useQuery } from '@tanstack/react-query';
import { getProfile, updateProfile } from '../lib/api';
import { useAuthStore } from '../lib/authStore';
import { Icon } from '../lib/icons';

/**
 * Persistent indicator shown while an administrator is "using the system as"
 * one or more roles (self-impersonation for testing). It is keyed off the
 * user's REAL ability to impersonate (`viewAs.canImpersonate`), so the Reset
 * control never disappears even when the impersonated role hides everything
 * else. Resetting clears the override and reloads so the whole app re-derives
 * permissions. Source of truth is the same ['profile'] query the rest of the
 * app uses (React Query dedupes the fetch).
 */
export function ImpersonationBanner() {
  const status = useAuthStore((s) => s.status);
  const { data } = useQuery({
    queryKey: ['profile'],
    queryFn: getProfile,
    enabled: status === 'authenticated',
    staleTime: 5 * 60 * 1000,
  });
  const reset = useMutation({
    mutationFn: () => updateProfile({ viewAsRoleIds: [] }),
    onSuccess: () => window.location.reload(),
  });

  const viewAs = data?.viewAs;
  if (!viewAs?.canImpersonate || viewAs.roleIds.length === 0) return null;

  const names = viewAs.roleIds
    .map((id) => viewAs.roles.find((r) => r.id === id)?.name)
    .filter(Boolean)
    .join(', ');

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        padding: 'var(--space-2) var(--space-3)',
        marginBottom: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--accent-primary-soft)',
        color: 'var(--accent-primary)',
        border: '1px solid var(--accent-primary)',
        fontSize: 'var(--text-sm)',
        fontWeight: 600,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
        <Icon name="view" size={15} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Viewing as <strong>{names || '…'}</strong> — your administrator access is reduced for testing.
        </span>
      </span>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={() => reset.mutate()}
        disabled={reset.isPending}
        style={{ flexShrink: 0 }}
      >
        {reset.isPending ? 'Resetting…' : 'Reset'}
      </button>
    </div>
  );
}
